import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Container,
  Fade,
  Grid,
  Grow,
  Link,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowIcon from '@mui/icons-material/ArrowForwardIosRounded';
import BoltIcon from '@mui/icons-material/BoltRounded';
import CalendarIcon from '@mui/icons-material/CalendarMonthRounded';
import GroupsIcon from '@mui/icons-material/Groups2Rounded';
import InsightsIcon from '@mui/icons-material/InsightsRounded';
import LightbulbIcon from '@mui/icons-material/LightbulbRounded';
import ManageAccountsIcon from '@mui/icons-material/ManageAccountsRounded';
import MagicIcon from '@mui/icons-material/AutoAwesomeRounded';
import SecurityIcon from '@mui/icons-material/SecurityRounded';
import StarIcon from '@mui/icons-material/StarRounded';
import SupportIcon from '@mui/icons-material/SupportAgentRounded';
import VolunteerIcon from '@mui/icons-material/VolunteerActivismRounded';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import RegistrationStepper from '../../components/RegistrationStepper';

const steps = ['Choose Role', 'Create Account', 'Start Exploring'];
const currentStep = 1;
const totalSteps = steps.length;

const RegistrationPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [hoveredCard, setHoveredCard] = useState(null);

  const roles = useMemo(
    () => [
      {
        id: 'student',
        title: 'Student Explorer',
        subtitle: 'Perfect for learners & collaborators',
        description: 'Discover AI-guided learning pathways, curated events, and vibrant peer communities.',
        iconEmoji: '🎓',
        color: '#1E3A8A',
        lightColor: '#1E3A8A',
        gradient: 'linear-gradient(135deg, #0D1C4F 0%, #1E3A8A 100%)',
        hoverGradient: 'linear-gradient(135deg, #1E3A8A 0%, #0D1C4F 100%)',
        shadowColor: 'rgba(30, 58, 138, 0.35)',
        stats: { mentors: '120+', opportunities: '1k+' },
        ctaText: 'Create Student Account',
        cardBg: 'linear-gradient(135deg, #ffffff 0%, #EFF6FF 100%)',
        features: [
          { icon: LightbulbIcon, text: 'Personalised skill journeys', desc: 'Roadmaps that adapt as you complete milestones.' },
          { icon: BoltIcon, text: 'Always-on AI mentor', desc: 'Instant answers, revision prompts, and study boosters.' },
          { icon: GroupsIcon, text: 'Peer study squads', desc: 'Collaborate with goal-based cohorts across colleges.' },
          { icon: StarIcon, text: 'Event recommendations', desc: 'Hand-picked hackathons, projects, and internships.' },
        ],
      },
      {
        id: 'club-member',
        title: 'Club Leader',
        subtitle: 'Empower your campus initiatives',
        description: 'Manage events, track engagement, and broadcast achievements with ease.',
        iconEmoji: '🚀',
        color: '#016f57',
        lightColor: '#016f57',
        gradient: 'linear-gradient(135deg, #016f57 0%, #019a78 100%)',
        hoverGradient: 'linear-gradient(135deg, #019a78 0%, #016f57 100%)',
        shadowColor: 'rgba(1, 111, 87, 0.35)',
        stats: { clubs: '50+', events: '500+' },
        ctaText: 'Create Club Account',
        cardBg: 'linear-gradient(135deg, #fafffe 0%, #f0fdf4 100%)',
        features: [
          { icon: CalendarIcon, text: 'Smart event planning', desc: 'Templates, reminders, and AI-written summaries.' },
          { icon: ManageAccountsIcon, text: 'Member engagement hub', desc: 'Track roles, achievements, and volunteer time.' },
          { icon: InsightsIcon, text: 'Impact analytics', desc: 'Understand reach with dashboards.' },
          { icon: MagicIcon, text: 'Showcase automation', desc: 'Generate recap posts, and shareable highlights.' },
        ],
      },
    ],
    [],
  );

  const handleRoleSelect = (roleId) => {
    if (roleId === 'student') {
      navigate('/signup/student');
      return;
    }
    if (roleId === 'club-member') {
      navigate('/signup/club-member');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#fafbfc' }}>
      <Navbar />

      <Box
        sx={{
          flexGrow: 1,
          background:
            'radial-gradient(ellipse at top, rgba(30, 58, 138, 0.03) 0%, transparent 50%), linear-gradient(135deg, #fafbfc 0%, #f1f5f9 100%)',
          pt: 0,
          pb: 8,
        }}
      >
        <Box sx={{ px: { xs: 2, md: 0 }, pt: 0, pb: 0 }}>
          <RegistrationStepper
            steps={steps}
            currentStep={currentStep}
            totalSteps={totalSteps}
            progressClassName="progress-bar"
          />
        </Box>

        <Container maxWidth="xl">
          <Fade in timeout={1000}>
            <Box>
              <Box sx={{ textAlign: 'center', mb: 8 }}>
                <Typography
                  variant={isMobile ? 'h3' : 'h2'}
                  component="h1"
                  sx={{
                    fontWeight: 800,
                    background: 'linear-gradient(135deg, #1e293b 0%, #475569 50%, #1E3A8A 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 3,
                    fontSize: isMobile ? '2.5rem' : '3.5rem',
                    letterSpacing: '-0.02em',
                  }}
                >
                  Choose your role to get started on{' '}
                  <Box component="span" sx={{ color: '#1E3A8A', fontWeight: 900 }}>
                    PeerCampus
                  </Box>
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    color: '#64748b',
                    fontWeight: 400,
                    maxWidth: 700,
                    mx: 'auto',
                    lineHeight: 1.6,
                    mb: 4,
                  }}
                >
                  Join thousands of students and club leaders already transforming their college experience.
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
                  <Chip
                    label="🚀 AI-Powered Learning"
                    sx={{
                      backgroundColor: 'rgba(1, 111, 87, 0.1)',
                      color: '#016f57',
                      fontWeight: 600,
                      px: 2,
                      py: 1,
                    }}
                  />
                  <Chip
                    label="🎯 Smart Networking"
                    sx={{
                      backgroundColor: 'rgba(1, 111, 87, 0.1)',
                      color: '#016f57',
                      fontWeight: 600,
                      px: 2,
                      py: 1,
                    }}
                  />
                  <Chip
                    label="⚡ Real-time Collaboration"
                    sx={{
                      backgroundColor: 'rgba(1, 111, 87, 0.1)',
                      color: '#016f57',
                      fontWeight: 600,
                      px: 2,
                      py: 1,
                    }}
                  />
                </Box>
              </Box>

              <Container maxWidth={false} sx={{ maxWidth: '1600px', mx: 'auto', px: 3 }}>
                <Grid container spacing={4} justifyContent="center" sx={{ minHeight: 'auto' }}>
                  {roles.map((role, index) => (
                    <Grid
                      item
                      xs={12}
                      lg={6}
                      key={role.id}
                      sx={{ display: 'flex', justifyContent: 'center', maxWidth: { xs: '100%', lg: '600px' } }}
                    >
                      <Grow in timeout={1200 + index * 400}>
                        <Card
                          role="button"
                          tabIndex={0}
                          aria-label={`Select ${role.title} role`}
                          onMouseEnter={() => setHoveredCard(role.id)}
                          onMouseLeave={() => setHoveredCard(null)}
                          onClick={() => handleRoleSelect(role.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleRoleSelect(role.id);
                            }
                          }}
                          sx={{
                            width: '100%',
                            maxWidth: { xs: '100%', sm: '450px', md: '500px', lg: '550px' },
                            minHeight: { xs: '550px', sm: '680px' },
                            cursor: 'pointer',
                            border: `2px solid ${hoveredCard === role.id ? role.color : 'transparent'}`,
                            borderRadius: 4,
                            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                            background: role.cardBg,
                            position: 'relative',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow:
                              hoveredCard === role.id
                                ? `0 20px 40px -12px ${role.shadowColor}, 0 8px 16px -4px rgba(0, 0, 0, 0.1)`
                                : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                            transform: hoveredCard === role.id ? 'translateY(-8px) scale(1.01)' : 'translateY(0) scale(1)',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              height: 4,
                              background: role.gradient,
                              transform: hoveredCard === role.id ? 'scaleX(1)' : 'scaleX(0)',
                              transformOrigin: 'left',
                              transition: 'transform 0.3s ease',
                            },
                            '&:focus': {
                              outline: `3px solid ${role.color}40`,
                              outlineOffset: 4,
                            },
                          }}
                        >
                          <Box
                            sx={{
                              textAlign: 'center',
                              px: { xs: 3, sm: 4 },
                              pt: { xs: 3, sm: 4 },
                              pb: { xs: 2, sm: 3 },
                              minHeight: { xs: '160px', sm: '200px' },
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                            }}
                          >
                            <Avatar
                              sx={{
                                width: { xs: 50, sm: 60, md: 65, lg: 70 },
                                height: { xs: 50, sm: 60, md: 65, lg: 70 },
                                mx: 'auto',
                                mb: 2,
                                background: role.gradient,
                                fontSize: { xs: '1.4rem', sm: '1.6rem', md: '1.8rem', lg: '2rem' },
                                boxShadow: `0 6px 20px ${role.shadowColor}`,
                                transform: hoveredCard === role.id ? 'scale(1.05)' : 'scale(1)',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              }}
                            >
                              {role.iconEmoji}
                            </Avatar>

                            <Typography
                              variant="h4"
                              component="h2"
                              sx={{
                                fontWeight: 700,
                                color: '#1e293b',
                                mb: 1,
                                fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.5rem', lg: '1.6rem' },
                              }}
                            >
                              {role.title}
                            </Typography>

                            <Typography
                              variant="subtitle1"
                              sx={{
                                color: role.color,
                                fontWeight: 600,
                                fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.95rem', lg: '1rem' },
                                mb: 2,
                              }}
                            >
                              {role.subtitle}
                            </Typography>

                            <Typography
                              variant="body1"
                              sx={{
                                color: '#64748b',
                                lineHeight: 1.5,
                                fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem', lg: '0.9rem' },
                                px: { xs: 0, sm: 1 },
                              }}
                            >
                              {role.description}
                            </Typography>
                          </Box>

                          {/*
                          <Box
                            sx={{
                              px: { xs: 3, sm: 4 },
                              pb: { xs: 2, sm: 3 },
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Stack direction="row" spacing={{ xs: 3, sm: 4 }} justifyContent="center">
                              {Object.entries(role.stats).map(([key, value]) => (
                                <Box key={key} sx={{ textAlign: 'center' }}>
                                  <Typography
                                    variant="h4"
                                    sx={{
                                      fontWeight: 800,
                                      color: role.color,
                                      fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.5rem', lg: '1.6rem' },
                                      mb: 0.5,
                                      lineHeight: 1,
                                    }}
                                  >
                                    {value}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: '#64748b',
                                      fontWeight: 500,
                                      fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem', lg: '0.8rem' },
                                    }}
                                  >
                                    {key.replace(/([A-Z])/g, ' $1').trim()}
                                  </Typography>
                                </Box>
                              ))}
                            </Stack>
                          </Box>
                          */}

                          <Box
                            sx={{
                              px: { xs: 3, sm: 4 },
                              flex: 1,
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'flex-start',
                            }}
                          >
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                color: '#1e293b',
                                mb: { xs: 1.5, sm: 2, md: 3 },
                                textAlign: 'center',
                                fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.95rem', lg: '1rem' },
                              }}
                            >
                              Key Features
                            </Typography>

                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                                gap: { xs: 1, sm: 1.5, md: 2 },
                                gridTemplateRows: {
                                  xs: 'repeat(4, auto)',
                                  md: 'repeat(2, 85px)',
                                  lg: 'repeat(2, 90px)',
                                },
                                mb: { xs: 0.5, sm: 1, md: 2 },
                              }}
                            >
                              {role.features.map((feature, idx) => {
                                const FeatureIcon = feature.icon;
                                return (
                                  <Box
                                    key={idx}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      p: { xs: 1, sm: 1.2, md: 1.5, lg: 2 },
                                      borderRadius: 3,
                                      background:
                                        hoveredCard === role.id ? `${role.lightColor}30` : 'rgba(255, 255, 255, 0.7)',
                                      transition: 'all 0.3s ease',
                                      minHeight: { xs: '60px', sm: '65px' },
                                      boxSizing: 'border-box',
                                    }}
                                  >
                                    <Avatar
                                      sx={{
                                        width: { xs: 28, sm: 30, md: 34, lg: 36 },
                                        height: { xs: 28, sm: 30, md: 34, lg: 36 },
                                        backgroundColor: `${role.color}15`,
                                        color: role.color,
                                        mr: { xs: 1, sm: 1.2, md: 1.5 },
                                        flexShrink: 0,
                                      }}
                                    >
                                      <FeatureIcon sx={{ fontSize: { xs: 14, sm: 16, md: 18 } }} />
                                    </Avatar>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: '#475569',
                                          fontWeight: 600,
                                          fontSize: { xs: '0.7rem', sm: '0.72rem', md: '0.78rem' },
                                          lineHeight: 1.3,
                                          mb: { xs: 0.2, sm: 0.3, md: 0.5 },
                                          wordWrap: 'break-word',
                                        }}
                                      >
                                        {feature.text}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: '#64748b',
                                          fontSize: { xs: '0.6rem', sm: '0.62rem', md: '0.68rem' },
                                          lineHeight: 1.2,
                                        }}
                                      >
                                        {feature.desc}
                                      </Typography>
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>

                          <Box sx={{ p: { xs: '0.5rem 3rem 1.5rem 3rem', sm: '0.5rem 4rem 2rem 4rem' } }}>
                            <Button
                              variant="contained"
                              endIcon={<ArrowIcon sx={{ fontSize: 18 }} />}
                              onClick={() => handleRoleSelect(role.id)}
                              fullWidth
                              sx={{
                                background: role.gradient,
                                color: 'white',
                                py: { xs: 1.8, sm: 2, md: 2.3 },
                                px: 4,
                                borderRadius: 3,
                                fontWeight: 700,
                                textTransform: 'none',
                                fontSize: { xs: '0.85rem', sm: '0.9rem', md: '1rem' },
                                boxShadow: `0 4px 15px ${role.shadowColor}`,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                  background: role.hoverGradient,
                                  transform: 'translateY(-1px)',
                                  boxShadow: `0 6px 20px ${role.shadowColor}`,
                                },
                              }}
                            >
                              {role.ctaText}
                            </Button>
                          </Box>
                        </Card>
                      </Grow>
                    </Grid>
                  ))}
                </Grid>

                <Box sx={{ textAlign: 'center', mt: 8, mb: 6 }}>
                  <Typography variant="body1" sx={{ color: '#64748b', mb: 1 }}>
                    Already have an account?{' '}
                    <Link
                      href="/login"
                      sx={{
                        color: '#016f57',
                        textDecoration: 'none',
                        fontWeight: 600,
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          width: '0',
                          height: '2px',
                          bottom: '-2px',
                          left: 0,
                          background: '#016f57',
                          transition: 'width 0.3s ease',
                        },
                        '&:hover::after': {
                          width: '100%',
                        },
                      }}
                    >
                      Login here
                    </Link>
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                    Choose carefully — roles cannot be changed after registration.
                  </Typography>
                </Box>

                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 3, sm: 4 },
                    borderRadius: 4,
                    background: 'rgba(255, 255, 255, 0.82)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    maxWidth: '800px',
                    mx: 'auto',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 3, textAlign: 'center' }}>
                    Trusted by thousands across India
                  </Typography>
                  <Stack
                    direction={isMobile ? 'column' : 'row'}
                    spacing={isMobile ? 2 : 4}
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: { xs: 1, sm: 1.5 },
                        textAlign: 'center',
                      }}
                    >
                      <Avatar sx={{ width: 32, height: 32, backgroundColor: '#016f57', color: 'white' }}>
                        <SecurityIcon sx={{ fontSize: 18 }} />
                      </Avatar>
                      <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500, mt: { xs: 0.5, sm: 0 } }}>
                        Secure & Private
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: { xs: 1, sm: 1.5 },
                        textAlign: 'center',
                      }}
                    >
                      <Avatar sx={{ width: 32, height: 32, backgroundColor: '#016f57', color: 'white' }}>
                        <VolunteerIcon sx={{ fontSize: 18 }} />
                      </Avatar>
                      <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500, mt: { xs: 0.5, sm: 0 } }}>
                        Fast to Join
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: { xs: 1, sm: 1.5 },
                        textAlign: 'center',
                      }}
                    >
                      <Avatar sx={{ width: 32, height: 32, backgroundColor: '#016f57', color: 'white' }}>
                        <SupportIcon sx={{ fontSize: 18 }} />
                      </Avatar>
                      <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500, mt: { xs: 0.5, sm: 0 } }}>
                        24/7 Support
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Container>
            </Box>
          </Fade>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};

export default RegistrationPage;